import { useState, useEffect } from "react";
import {
  useOneSizeGrp,
  useCreateSizeGrp,
  useUpdateSizeGrp,
} from "http-hooks/sizegrp";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import { Switch } from "@headlessui/react";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Constants moved to top to prevent re-renders
const typeOptions = [
  { value: "SHP", label: "Shape" },
  { value: "CLR", label: "Color" },
  { value: "SZ", label: "Size" },
];

const rateConditionOptions = [
  { value: 1, label: "With Rate" },
  { value: 0, label: "Without Rate" },
];

const initialSizeGrp = {
  szgrpname: "",
  type: "",
  grpcond: 1, // Default to "With Rate"
  colnos: 1, // Default to 1 column
  ag_status: 1, // Default to active
};

// Add loading skeleton component
const FormSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-7 bg-gray-200 rounded w-1/4 mb-8"></div>
    <div className="space-y-6">
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
    </div>
  </div>
);

export default function SetSizeGrp({ szgrpid, onClose, onSuccess }) {
  const [sizeGrp, setSizeGrp] = useState(initialSizeGrp);

  const isEdit = !!szgrpid;

  // Fetch existing data for edit mode
  const { data: existingSizeGrp, isLoading: isLoadingSizeGrp } = useOneSizeGrp(
    szgrpid,
    isEdit
  );

  // Mutations
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateSizeGrp();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateSizeGrp();

  // Load existing data for edit mode
  useEffect(() => {
    if (existingSizeGrp) {
      setSizeGrp((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach(key => {
          updatedDetails[key] = existingSizeGrp[key] ?? prev[key];
        });
        return updatedDetails;
      });
    }
  }, [existingSizeGrp]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSizeGrp((prev) => ({
      ...prev,
      [name]: name === "colnos" ? Number(value) : value,
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setSizeGrp((prev) => ({ ...prev, [name]: selectedOption?.value ?? "" }));
  };

  const handleStatusChange = (e) => {
    const val = e ? 1 : 0;
    setSizeGrp((prev) => ({ ...prev, ag_status: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        szgrpname: sizeGrp.szgrpname.trim(),
        type: sizeGrp.type,
        grpcond: sizeGrp.grpcond,
        colnos: sizeGrp.colnos,
      };

      // Include status only for edit mode
      if (isEdit) {
        formData.ag_status = sizeGrp.ag_status;
      }

      if (isEdit) {
        await mutateUpdate({ id: szgrpid, data: formData });
      } else {
        await mutateCreate(formData);
      }
      onSuccess();
    } catch (error) {
      // Error toast already shown by service
      console.error("Submit failed:", error);
    }
  };

  if (szgrpid && isLoadingSizeGrp) {
    return <FormSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-8">
        <div className="border-b border-gray-900/10 pb-10">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            {szgrpid ? "Update" : "Add"} Order Print Group
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="col-span-full">
              <Input
                type="text"
                name="szgrpname"
                id="szgrpname"
                label="Group Name"
                value={sizeGrp.szgrpname}
                onChange={handleInputChange}
                placeholder="Enter group name (max 20 characters)"
                maxLength={20}
              />
            </div>

            <div className="col-span-full">
              <Select
                label="Group Type"
                options={typeOptions}
                value={typeOptions.find((option) => option.value === sizeGrp.type)}
                onChange={(selected) => handleSelectChange("type", selected)}
                placeholder="Select group type"
                isClearable={false}
              />
            </div>

            <div className="col-span-full">
              <Select
                label="With/Without Rate"
                options={rateConditionOptions}
                value={rateConditionOptions.find(
                  (option) => option.value === sizeGrp.grpcond
                )}
                onChange={(selected) => handleSelectChange("grpcond", selected)}
                placeholder="Select rate condition"
                isClearable={false}
              />
            </div>

            <div className="col-span-full">
              <Input
                type="number"
                name="colnos"
                id="colnos"
                label="Number of Columns"
                value={sizeGrp.colnos}
                onChange={handleInputChange}
                placeholder="Enter number of columns"
                min={1}
                max={sizeGrp.grpcond === 1 ? 5 : 10}
                helperText={
                  sizeGrp.grpcond === 1
                    ? "Maximum 5 columns for 'With Rate' groups"
                    : "Maximum 10 columns"
                }
              />
            </div>

            {/* Status Toggle - Only for edit mode */}
            {isEdit && (
              <div className="col-span-full">
                <label
                  htmlFor="ag_status"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Status
                </label>
                <Switch
                  checked={sizeGrp.ag_status}
                  onChange={handleStatusChange}
                  className={classNames(
                    sizeGrp.ag_status
                      ? "bg-indigo-600"
                      : "bg-gray-200",
                    "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      sizeGrp.ag_status
                        ? "translate-x-5"
                        : "translate-x-0",
                      "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    )}
                  />
                </Switch>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
          onClick={onClose}
          disabled={isCreating || isUpdating}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isCreating || isUpdating}
          className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreating || isUpdating ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
} 